import { Injectable } from "@angular/core";

interface ActivationNotice {
  email?: string;
  expiresAt: number;
}

const STORAGE_KEY = "ipa.activationNotice";
const TTL_MS = 10 * 60 * 1000;

/**
 * Carries "account just activated" from /invite/set-password to the login page. The API redirects
 * with a full page load, so an in-memory flag (like CredentialHandoffService) would be lost.
 * sessionStorage survives it, stays on this origin and tab, and never holds the password. A pending
 * notice also keeps the login page from leaving for the portal when it has no OIDC context.
 */
@Injectable({ providedIn: "root" })
export class ActivationNoticeService {
  public stage(email?: string): void {
    const notice: ActivationNotice = { email, expiresAt: Date.now() + TTL_MS };
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(notice));
    } catch (err) {
      console.warn("ActivationNoticeService.stage - sessionStorage unavailable, banner will not be shown.", err);
    }
  }

  /** Returns the pending notice once and clears it; null when none, expired or unreadable. */
  public consume(): { email?: string } | null {
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem(STORAGE_KEY);
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (err) {
      console.warn("ActivationNoticeService.consume - sessionStorage unavailable.", err);
      return null;
    }
    if (!raw) return null;
    try {
      const notice = JSON.parse(raw) as ActivationNotice;
      if (!notice.expiresAt || notice.expiresAt < Date.now()) return null;
      return { email: notice.email };
    } catch {
      return null;
    }
  }
}
